<template>
  <div class="pt-6">
    <!-- Tenant picker: a slim bar, same width as the module cards below. -->
    <v-card outlined class="ai-card mb-6">
      <div class="d-flex align-center flex-wrap px-4 py-3">
        <v-avatar size="36" :color="avatarColor" class="mr-3">
          <v-icon small color="primary">mdi-domain</v-icon>
        </v-avatar>
        <v-autocomplete
          v-model="domain"
          :items="tenantItems"
          :loading="$apollo.queries.owners.loading"
          label="Tenant"
          outlined
          dense
          hide-details
          class="tenant-picker"
        ></v-autocomplete>
        <v-spacer></v-spacer>
        <span
          v-if="domain && modules.length"
          class="caption grey--text ml-4"
          :class="mutedShade"
        >
          {{ modules.length }} AI module{{ modules.length === 1 ? "" : "s" }} ·
          {{ enabledCount }} enabled
        </span>
      </div>
      <v-progress-linear
        :active="!!domain && loading"
        indeterminate
        absolute
        bottom
        height="2"
      ></v-progress-linear>
    </v-card>

    <v-alert v-if="errorMessage" type="error" dense text class="mb-6">
      {{ errorMessage }}
    </v-alert>

    <v-card
      v-if="!domain || (!loading && !errorMessage && modules.length === 0)"
      outlined
      class="ai-card ai-empty pa-10"
    >
      <v-avatar size="64" :color="avatarColor" class="mb-3">
        <v-icon size="32" color="primary">
          {{ domain ? "mdi-robot" : "mdi-domain" }}
        </v-icon>
      </v-avatar>
      <div class="subtitle-1 font-weight-medium">
        {{ domain ? "No AI modules are registered" : "Pick a tenant" }}
      </div>
      <div class="body-2 grey--text mt-1" :class="mutedShade">
        {{
          domain
            ? "There is nothing to set up yet."
            : "Its AI module settings will show here."
        }}
      </div>
    </v-card>

    <ai-module-card
      v-for="m in modules"
      :key="`${m.setting.domain}:${m.setting.module}`"
      :domain="m.setting.domain"
      :definition="m.definition"
      :setting="m.setting"
      class="mb-6"
    ></ai-module-card>
  </div>
</template>

<script>
import AiModuleCard from "./AiModuleCard";
import {
  AI_SETUP_OWNERS_QUERY,
  AI_MODULE_REGISTRY_QUERY,
  AI_MODULE_SETTINGS_QUERY,
} from "./ai-setup-graphql";

const gqlError = (e) =>
  e && e.graphQLErrors && e.graphQLErrors.length
    ? e.graphQLErrors.map((g) => g.message).join(", ")
    : (e && e.message) || "Request failed.";

export default {
  name: "ai-setup-setup-tab",

  components: { AiModuleCard },

  data() {
    const own = this.$store.getters.userConfig.domain;
    return {
      domain: own ? own.trim() : null,
      owners: null,
      registry: null,
      settings: null,
      errorMessage: "",
    };
  },

  apollo: {
    owners: {
      query: AI_SETUP_OWNERS_QUERY,
    },
    registry: {
      query: AI_MODULE_REGISTRY_QUERY,
      fetchPolicy: "network-only",
      update: (data) => data.aiModuleRegistry,
      error(e) {
        this.errorMessage = gqlError(e);
      },
    },
    settings: {
      query: AI_MODULE_SETTINGS_QUERY,
      fetchPolicy: "network-only",
      variables() {
        return { domain: this.domain };
      },
      skip() {
        return !this.domain;
      },
      update: (data) => data.aiModuleSettings,
      result() {
        this.errorMessage = "";
      },
      error(e) {
        this.errorMessage = gqlError(e);
      },
    },
  },

  computed: {
    mutedShade() {
      return this.$vuetify.theme.dark ? "text--lighten-1" : "text--darken-1";
    },
    avatarColor() {
      return this.$vuetify.theme.dark ? "grey darken-3" : "blue lighten-5";
    },
    enabledCount() {
      return this.modules.filter((m) => m.setting.enabled).length;
    },
    tenantItems() {
      const edges = (this.owners && this.owners.edges) || [];
      return edges
        .map((e) => e.node)
        .filter((o) => o.domain && o.domain.trim())
        .map((o) => ({
          text: `${(o.name || "").trim()} (${o.domain.trim()})`,
          value: o.domain.trim(),
        }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    loading() {
      return (
        this.$apollo.queries.registry.loading ||
        this.$apollo.queries.settings.loading
      );
    },
    // One card per registered module that the tenant's settings came back for.
    // #11522: only settings returned for the picked tenant. Right after a switch
    // this.settings still holds the previous tenant's result; showing it under the
    // new tenant displayed wrong values and a Save would have written them there.
    modules() {
      if (!this.registry || !this.settings) return [];
      const byKey = {};
      this.registry.forEach((d) => (byKey[d.key] = d));
      return this.settings
        .filter((s) => (s.domain || "").trim() === this.domain)
        .filter((s) => byKey[s.module])
        .map((s) => ({ definition: byKey[s.module], setting: s }));
    },
  },
};
</script>

<style scoped>
.tenant-picker {
  max-width: 420px;
  min-width: 240px;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/admin/ai-setup/AiSetupSetupTab.vue